import { useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Link2, MessageSquare, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field, Input, Select, Textarea } from "@/components/ui/input";
import { Badge } from "@/components/ui/misc";
import { Sheet } from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { useMe } from "@/hooks/useMe";
import { useProjects, useTask, useTaskMutations, useTasks } from "@/hooks/useWork";
import { api } from "@/lib/api";
import { fmtDateTime, toDateInput, toLocalInput } from "@/lib/dates";
import { formatRelative } from "@/lib/utils";
import type { TaskStatus } from "@/lib/types";
import { PriorityPicker } from "./PriorityBadge";
import { t } from "@shared/i18n";

export const STATUS_LABELS: Record<TaskStatus, string> = { todo: t("À faire"), in_progress: t("En cours"), blocked: t("Bloquée"), done: t("Terminée"), cancelled: t("Annulée") };
const ACTION_LABELS: Record<string, string> = {
  created: t("Création"),
  updated: t("Modification"),
  created_by_coach: t("Créée par le Coach IA (validée)"),
  updated_by_coach: t("Modifiée par le Coach IA (validée)"),
  created_by_mission: t("Créée par Mission Mode"),
  updated_by_planning: t("Planifiée"),
  created_by_automation: t("Créée par une automatisation"),
};
const FIELD_LABELS: Record<string, string> = { title: t("titre"), description: t("description"), status: t("statut"), priority: t("priorité"), dueAt: t("échéance"), projectId: t("projet"), estimatedMinutes: t("durée estimée"), scheduledStart: t("créneau"), assigneeId: t("responsable") };

type Draft = {
  title: string;
  description: string;
  status: TaskStatus;
  priority: number;
  dueAt: string;
  scheduledStart: string;
  scheduledEnd: string;
  estimatedMinutes: string;
  actualMinutes: string;
  projectId: string;
  category: string;
  tags: string;
  energy: string;
  dependsOn: string[];
  assigneeId: string;
};

const empty = (defaults: Partial<Draft> = {}): Draft => ({ title: "", description: "", status: "todo", priority: 3, dueAt: "", scheduledStart: "", scheduledEnd: "", estimatedMinutes: "", actualMinutes: "", projectId: "", category: "", tags: "", energy: "", dependsOn: [], assigneeId: "", ...defaults });

/** Création (taskId = null) ou édition complète d'une tâche. */
export function TaskSheet({ open, onOpenChange, taskId, defaults }: { open: boolean; onOpenChange: (o: boolean) => void; taskId: string | null; defaults?: Partial<Draft> }) {
  const detail = useTask(open ? taskId : null);
  const projects = useProjects();
  const allTasks = useTasks({ view: "all" });
  const { create, update, remove } = useTaskMutations();
  const { data: me } = useMe();
  const shared = me ? !me.workspace.isPersonal : false;
  const team = useQuery({ queryKey: ["team"], enabled: open && shared, queryFn: async () => (await api<{ data: { members: { userId: string; name: string; role: string }[] } }>("/api/team")).data });
  const [d, setD] = useState<Draft>(empty(defaults));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (!taskId) setD(empty(defaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  useEffect(() => {
    const t = detail.data;
    if (!t || !taskId) return;
    setD({
      title: t.title,
      description: t.description ?? "",
      status: t.status,
      priority: t.priority,
      dueAt: toDateInput(t.dueAt),
      scheduledStart: toLocalInput(t.scheduledStart),
      scheduledEnd: toLocalInput(t.scheduledEnd),
      estimatedMinutes: t.estimatedMinutes?.toString() ?? "",
      actualMinutes: t.actualMinutes?.toString() ?? "",
      projectId: t.projectId ?? "",
      category: t.category ?? "",
      tags: t.tags.join(", "),
      energy: t.energy ?? "",
      dependsOn: t.dependsOn,
      assigneeId: t.assigneeId ?? "",
    });
  }, [detail.data, taskId]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!d.title.trim()) return setError(t("Le titre est requis."));
    const body = {
      title: d.title.trim(),
      description: d.description.trim() || null,
      status: d.status,
      priority: d.priority,
      dueAt: d.dueAt || null,
      scheduledStart: d.scheduledStart || null,
      scheduledEnd: d.scheduledEnd || null,
      estimatedMinutes: d.estimatedMinutes ? Number(d.estimatedMinutes) : null,
      actualMinutes: d.actualMinutes ? Number(d.actualMinutes) : null,
      projectId: d.projectId || null,
      category: d.category.trim() || null,
      tags: d.tags.split(",").map((s) => s.trim()).filter(Boolean),
      energy: (d.energy || null) as "high" | "medium" | "low" | null,
      dependsOn: d.dependsOn,
      ...(shared ? { assigneeId: d.assigneeId || null } : {}),
    };
    try {
      if (taskId) await update.mutateAsync({ id: taskId, ...body });
      else await create.mutateAsync(body);
      onOpenChange(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const candidates = (allTasks.data ?? []).filter((t) => t.id !== taskId && t.status !== "cancelled");

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={taskId ? t("Modifier la tâche") : t("Nouvelle tâche")}
      footer={
        <div className="flex items-center gap-2">
          {taskId && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("Supprimer la tâche")}
              onClick={async () => {
                if (!confirm(t("Supprimer définitivement cette tâche ?"))) return;
                await remove.mutateAsync(taskId);
                onOpenChange(false);
              }}
            >
              <Trash2 className="size-4 text-accent" />
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("Annuler")}
          </Button>
          <Button onClick={save} loading={create.isPending || update.isPending}>
            {t("Enregistrer")}
          </Button>
        </div>
      }
    >
      {taskId && detail.isLoading ? (
        <Spinner />
      ) : (
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Field label={t("Titre")} error={error}>
            {(p) => <Input {...p} autoFocus value={d.title} maxLength={300} onChange={(e) => set("title", e.target.value)} placeholder={t("Ex. Envoyer la proposition à Martin")} />}
          </Field>
          <Field label={t("Description")}>{(p) => <Textarea {...p} value={d.description} onChange={(e) => set("description", e.target.value)} rows={3} />}</Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("Statut")}>
              {(p) => (
                <Select {...p} value={d.status} onChange={(e) => set("status", e.target.value as TaskStatus)}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t("Projet")}>
              {(p) => (
                <Select {...p} value={d.projectId} onChange={(e) => set("projectId", e.target.value)}>
                  <option value="">{t("Aucun")}</option>
                  {projects.data?.map((pr) => (
                    <option key={pr.id} value={pr.id}>
                      {pr.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div>
            <p className="mb-1.5 text-[13px] font-medium">{t("Priorité")}</p>
            <PriorityPicker value={d.priority} onChange={(v) => set("priority", v)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("Échéance")}>{(p) => <Input {...p} type="date" value={d.dueAt} onChange={(e) => set("dueAt", e.target.value)} />}</Field>
            <Field label={t("Durée estimée (min)")}>{(p) => <Input {...p} type="number" min={5} step={5} value={d.estimatedMinutes} onChange={(e) => set("estimatedMinutes", e.target.value)} />}</Field>
            <Field label={t("Début du créneau")}>{(p) => <Input {...p} type="datetime-local" value={d.scheduledStart} onChange={(e) => set("scheduledStart", e.target.value)} />}</Field>
            <Field label={t("Fin du créneau")}>{(p) => <Input {...p} type="datetime-local" value={d.scheduledEnd} onChange={(e) => set("scheduledEnd", e.target.value)} />}</Field>
            <Field label={t("Énergie requise")}>
              {(p) => (
                <Select {...p} value={d.energy} onChange={(e) => set("energy", e.target.value)}>
                  <option value="">{t("Non précisée")}</option>
                  <option value="high">{t("Haute (concentration)")}</option>
                  <option value="medium">{t("Moyenne")}</option>
                  <option value="low">{t("Faible")}</option>
                </Select>
              )}
            </Field>
            {shared && (
              <Field label={t("Responsable")}>
                {(p) => (
                  <Select {...p} value={d.assigneeId} onChange={(e) => set("assigneeId", e.target.value)}>
                    <option value="">{t("Non assignée")}</option>
                    {team.data?.members.filter((m) => m.role !== "guest").map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            <Field label={t("Durée réelle (min)")}>{(p) => <Input {...p} type="number" min={0} step={5} value={d.actualMinutes} onChange={(e) => set("actualMinutes", e.target.value)} />}</Field>
            <Field label={t("Catégorie")}>{(p) => <Input {...p} value={d.category} maxLength={60} onChange={(e) => set("category", e.target.value)} />}</Field>
            <Field label={t("Étiquettes")} hint={t("Séparées par des virgules")}>{(p) => <Input {...p} value={d.tags} onChange={(e) => set("tags", e.target.value)} />}</Field>
          </div>
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-[13px] font-medium">
              <Link2 className="size-3.5" /> {t("Dépend de")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {d.dependsOn.map((id) => {
                const dep = candidates.find((x) => x.id === id);
                return (
                  <button key={id} type="button" onClick={() => set("dependsOn", d.dependsOn.filter((x) => x !== id))} className="rounded-full border border-border px-2.5 py-1 text-[12px] hover:border-accent" title={t("Retirer")}>
                    {dep?.title ?? t("Tâche")} {"×"}
                  </button>
                );
              })}
            </div>
            <Select
              aria-label={t("Ajouter une dépendance")}
              className="mt-2"
              value=""
              onChange={(e) => e.target.value && set("dependsOn", [...d.dependsOn, e.target.value])}
            >
              <option value="">{t("Ajouter une dépendance…")}</option>
              {candidates.filter((t) => !d.dependsOn.includes(t.id)).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </Select>
          </div>
          {taskId && shared && <Comments taskId={taskId} />}
          {taskId && detail.data && (
            <div className="space-y-3 border-t border-border pt-5">
              {detail.data.blocking.length > 0 && (
                <p className="text-sm text-muted">
                  {t("Bloque :")} {detail.data.blocking.map((b) => b.title).join(", ")}
                </p>
              )}
              <p className="flex items-center gap-1.5 text-[13px] font-medium">
                <History className="size-3.5" /> {t("Historique")}
              </p>
              <ol className="space-y-2">
                {detail.data.activity.map((a) => (
                  <li key={a.id} className="text-[13px]">
                    <span className="text-muted">{fmtDateTime(a.createdAt)}</span> · {ACTION_LABELS[a.action] ?? a.action}
                    {a.changes && (
                      <span className="text-muted"> — {Object.keys(a.changes).map((k) => FIELD_LABELS[k] ?? k).join(", ")}</span>
                    )}
                  </li>
                ))}
              </ol>
              {detail.data.source !== "user" && <Badge tone="primary">{t("Origine :")} {detail.data.source === "coach" ? t("Coach IA") : detail.data.source === "mission" ? t("Mission Mode") : detail.data.source}</Badge>}
            </div>
          )}
        </form>
      )}
    </Sheet>
  );
}

type Comment = { id: string; body: string; createdAt: string; userName: string | null };

/** Fil de discussion d'une tâche partagée (espaces d'équipe). */
function Comments({ taskId }: { taskId: string }) {
  const qc = useQueryClient();
  const key = ["task-comments", taskId];
  const list = useQuery({ queryKey: key, queryFn: async () => (await api<{ data: Comment[] }>(`/api/team/tasks/${taskId}/comments`)).data });
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api(`/api/team/tasks/${taskId}/comments`, { method: "POST", json: { body: body.trim() } });
      setBody("");
      await qc.invalidateQueries({ queryKey: key });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 border-t border-border pt-5">
      <p className="flex items-center gap-1.5 text-[13px] font-medium">
        <MessageSquare className="size-3.5" /> {t("Commentaires")}
      </p>
      <ul className="space-y-2">
        {list.data?.map((c) => (
          <li key={c.id} className="rounded-lg bg-surface px-3 py-2 text-sm">
            <p className="text-[12px] text-muted">
              <span className="font-medium text-fg">{c.userName ?? t("Ancien membre")}</span> · {formatRelative(c.createdAt)}
            </p>
            <p className="mt-0.5 whitespace-pre-wrap">{c.body}</p>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("Écrire un commentaire…")} aria-label={t("Nouveau commentaire")} />
        <Button type="button" variant="secondary" onClick={send} loading={busy}>
          {t("Envoyer")}
        </Button>
      </div>
    </div>
  );
}
